import React, { forwardRef, useState, useEffect } from 'react';
import isNil from 'lodash/isNil';
import cn from 'classnames';
import { Button } from '@progress/kendo-react-buttons';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const BaseButton = forwardRef(
  (
    {
      id,
      responsive = false,
      className,
      disabled,
      // Color theme
      primary,
      themeColor = 'base',
      // Content
      children,
      title,
      tooltip,
      // Telerik icon name
      icon,
      iconComponent = null,
      // Used if content is a not Telerik icon (like react-icons)
      iconOnly = false,
      // Show loading icon
      isLoading = false,
      // Click Handler
      onClick,
      // If onClick return a promise - use to show a loader
      isAsyncClick = false,
      ...props
    },
    ref,
  ) => {
    const content = iconOnly ? iconComponent : title ?? children;
    const isResponsive = responsive && !iconOnly && !isNil(content);
    const btnClassName = cn({ [className]: className, responsive: isResponsive, 'k-icon-button': iconOnly });
    const [loading, setLoading] = useState(isLoading);

    useEffect(() => {
      if (!isAsyncClick) {
        setLoading(isLoading);
      }
    }, [isLoading]);

    const handleClick = async (e) => {
      // Close all tooltips on button click.
      // Kendo has an issue when doing this causing it to stay open when a modal is over.
      document.querySelectorAll('.k-animation-container-fixed').forEach((container) => {
        container.querySelectorAll('[role="tooltip"]').forEach((tooltip) => {
          tooltip.remove();
        });
      });

      if (!isAsyncClick) {
        if (onClick) {
          onClick(e);
        }
        return;
      }

      setLoading(true);
      try {
        await onClick(e);
      } finally {
        setLoading(false);
      }
    };

    return (
      <Button
        id={id}
        ref={ref}
        type='button'
        shape='rectangle'
        rounded='medium'
        themeColor={primary ? 'primary' : themeColor}
        className={btnClassName}
        title={tooltip}
        disabled={disabled || loading}
        icon={loading ? 'loading' : icon}
        onClick={handleClick}
        data-testid={id}
        {...props}
      >
        {content}
      </Button>
    );
  },
);

BaseButton.displayName = 'BaseButton';

export default BaseButton;
